/**
 * The wireline & slickline hand's phone surface (fieldiq-api#1338, WL-2).
 * SNAKE_CASE on the wire; the field hooks camelize it ONCE so these screens
 * share the office's types and the sheet / run pieces.
 *
 *   /field/wl                                   my jobs today and my sheets (14 days)
 *   /field/wl/jobs/:id/sheets/:workDate         open that day's sheet for a unit
 *   /field/wl/sheets/:id                        the sheet: edit, runs, tests, drills, submit
 *   /field/wl/sheets/:id/runs/new               add a run (kind-specific form)
 *   /field/wl/sheets/:id/runs/:runId            one run; edit while the sheet is a draft
 *   /field/wl/sheets/:id/pressure-tests/new     the shared test form, posting to the sheet
 *
 * WHO RECORDS. Only the supervisor or the operator ON THIS SHEET'S CREW
 * records runs, pressure tests and drills, and changes the sheet (the API
 * refuses a helper 403 position_cannot_record / position_cannot_edit_crew).
 * A helper sees the whole sheet READ ONLY, with a line saying who records.
 * The buttons are drawn from the sheet's crew; the API decides.
 *
 * Warnings (rig-up test missing, misfire count, meter below its last reading)
 * are shown and never block; the rig-up 409 — when the tenant requires the
 * test — is a refusal, shown in the API's own sentence.
 */
import React, { useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, ChevronRight, ClipboardList, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { useWlEquipmentOptions, useWlLines, useWlMeta, useWlToolStrings, useWlUnit, useWlUsers } from '../../../hooks/useWirelineSlickline';
import {
  useAddWlFieldRun,
  useRecordWlFieldDrill,
  useRecordWlFieldTest,
  useRemoveWlFieldRecord,
  useSaveWlFieldSheet,
  useSubmitWlFieldSheet,
  useUpdateWlFieldRun,
  useWlFieldMySheets,
  useWlFieldSheet,
  useWlJobUnits,
} from '../../../hooks/useWlJobs';
import { pcReviewSkin } from '../../../lib/pressure-control';
import {
  canWriteWlSheets,
  wlCanRecordOnSheet,
  wlClockRange,
  wlDay,
  wlDefaultInstant,
  wlHours,
  wlJobRefusal,
  wlJobStatusSkin,
  wlRigUpRefusal,
  wlSheetStatusSkin,
} from '../../../lib/wireline-slickline-jobs';
import type { WlMeta } from '../../../types/wireline-slickline';
import type { WlFieldJob, WlSheet, WlSheetSummary, WlWarningEntry } from '../../../types/wireline-slickline-jobs';
import PressureControlTestNewScreen from '../../equipment/PressureControlTestNewScreen';
import { RecordDrillDialog } from '../../equipment/PressureControlDrillsScreen';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldEmpty,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';
import { RunForm, RunReadout, RunWarnings } from '../RunParts';
import { wlRunRefusal } from '../RunScreen';
import { sheetDrills, sheetTests, wlDrillLine, wlTestLine } from '../SheetLinks';
import { SheetEditor, SheetReadout, SheetRuns, SheetSummary, SheetWarnings } from '../SheetParts';
import { WL_INPUT, WL_SECONDARY, WL_SOUR_SKIN, WlDialog, WlField, WlFormError } from '../WlBits';

export const wlFieldSheetPath = (id: string) => `/field/wl/sheets/${id}`;
const fieldRunPath = (sheetId: string) => (runId: string) => `/field/wl/sheets/${sheetId}/runs/${runId}`;

const refusedOpen = (err: unknown) => {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 403 ? wlJobRefusal(err, 'You cannot open this day sheet.') : null;
};

// ─── My sheets ───────────────────────────────────────────────────────────────

function JobCard({ job, today }: { job: WlFieldJob; today: string | null }) {
  const open = job.todaySheetIds.length;
  const to = open === 1 ? wlFieldSheetPath(job.todaySheetIds[0]) : today ? `/field/wl/jobs/${job.id}/sheets/${today}` : null;
  const body = (
    <PtFieldCard className="flex min-h-[88px] items-center gap-3 px-4 py-4 active:bg-fieldiq-dark/50">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-base font-extrabold text-fieldiq-text">{job.jobNumber || '—'}</span>
          <PtFieldBadge skin={wlJobStatusSkin(job.status, job.statusLabel)} />
          {job.wellSour && <PtFieldBadge skin={WL_SOUR_SKIN} />}
        </div>
        <p className="mt-1 truncate text-base font-bold text-white">{job.customerName || '—'}</p>
        <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-fieldiq-text">
          <MapPin className="size-3.5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
          {job.wellName || '—'}
          {job.wellUwi || job.wellApiNumber ? ` · ${job.wellUwi || job.wellApiNumber}` : ''}
        </p>
        <p className="mt-1 flex items-center gap-1 text-xs text-fieldiq-muted">
          {open > 0 ? (
            <>
              <CheckCircle2 className="size-3.5 text-fi-green" aria-hidden="true" />
              {open === 1 ? "Today's sheet is open" : `${open} sheets open today`}
            </>
          ) : (
            'No sheet yet today'
          )}
        </p>
      </div>
      {to && <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />}
    </PtFieldCard>
  );
  return to ? (
    <Link to={to} className="block" data-testid="wl-my-job" aria-label={`${job.jobNumber} today's day sheet`}>
      {body}
    </Link>
  ) : (
    <div data-testid="wl-my-job">{body}</div>
  );
}

function SheetRow({ sheet }: { sheet: WlSheetSummary }) {
  return (
    <Link to={wlFieldSheetPath(sheet.id)} className="block" data-testid="wl-my-sheet">
      <PtFieldCard className="flex items-center gap-3 px-4 py-3 active:bg-fieldiq-dark/50">
        <ClipboardList className="size-5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-extrabold text-white">
            {wlDay(sheet.workDate)} · {sheet.jobNumber || '—'} · {sheet.unitNumber || '—'}
          </p>
          <p className="mt-0.5 text-xs text-fieldiq-text">
            {wlClockRange(sheet.startTime, sheet.endTime)} · {wlHours(sheet.sheetHours)} · {sheet.runCount} run{sheet.runCount === 1 ? '' : 's'}
          </p>
        </div>
        <PtFieldBadge skin={wlSheetStatusSkin(sheet.status, sheet.statusLabel)} />
      </PtFieldCard>
    </Link>
  );
}

export default function WlMySheetsScreen() {
  const query = useWlFieldMySheets();
  const jobs = query.data?.data.jobs ?? [];
  const sheets = query.data?.data.sheets ?? [];
  const today = query.data?.meta?.today ?? null;
  return (
    <PtFieldPage title="My Sheets" backTo="/field" testId="wl-my-sheets">
      <PtFieldQueryState query={query} subject="your day sheets">
        <section aria-labelledby="wl-group-jobs">
          <PtFieldSectionTitle>
            <span id="wl-group-jobs">Today{today ? ` · ${wlDay(today)}` : ''}</span>
          </PtFieldSectionTitle>
          {jobs.length === 0 ? (
            <PtFieldEmpty>You are not on any open wireline or slickline job. The office puts you on a unit&apos;s crew.</PtFieldEmpty>
          ) : (
            <div className="space-y-2">
              {jobs.map((j) => (
                <JobCard key={j.id} job={j} today={today} />
              ))}
            </div>
          )}
        </section>
        <section aria-labelledby="wl-group-recent">
          <PtFieldSectionTitle>
            <span id="wl-group-recent">Last 14 days · {sheets.length}</span>
          </PtFieldSectionTitle>
          {sheets.length === 0 ? (
            <PtFieldEmpty>No day sheet in the last 14 days.</PtFieldEmpty>
          ) : (
            <div className="space-y-2">
              {sheets.map((s) => (
                <SheetRow key={s.id} sheet={s} />
              ))}
            </div>
          )}
        </section>
      </PtFieldQueryState>
    </PtFieldPage>
  );
}

// ─── Open the day's sheet ────────────────────────────────────────────────────

const POSITIONS = [
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'operator', label: 'Operator' },
  { value: 'helper', label: 'Helper' },
];

export function WlFieldSheetDayScreen() {
  const { id, workDate } = useParams<{ id: string; workDate: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const mine = useWlFieldMySheets();
  const units = useWlJobUnits(id);
  const save = useSaveWlFieldSheet();
  const list = units.data?.data ?? [];
  const [unitId, setUnitId] = useState('');
  const chosen = unitId || (list.length === 1 ? list[0].unitId : '');
  const unit = useWlUnit(chosen || undefined);
  const crews = unit.data?.data?.crews ?? [];
  const [templateId, setTemplateId] = useState('');
  const [position, setPosition] = useState('operator');
  const [error, setError] = useState<string | null>(null);
  const existing = (mine.data?.data.sheets ?? []).filter((s) => s.jobId === id && s.workDate === workDate);
  const job = (mine.data?.data.jobs ?? []).find((j) => j.id === id) ?? null;
  const canWrite = canWriteWlSheets(user?.role);
  const refused = refusedOpen(units.error);

  // One unit, its sheet already open: go there.
  if (list.length === 1 && existing.length === 1) return <Navigate to={wlFieldSheetPath(existing[0].id)} replace />;
  const openFor = new Set(existing.map((s) => s.unitId));

  return (
    <PtFieldPage title={`Day sheet · ${wlDay(workDate)}`} backTo="/field/wl" testId="wl-field-sheet-day">
      {refused ? (
        <PtRefusal message={refused} testId="wl-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={units} subject="this job's units">
          <PtFieldCard className="space-y-4 px-4 py-4">
            <p className="text-sm text-fieldiq-text">
              {job?.jobNumber ?? '—'} · {job?.wellName || '—'}
            </p>
            {existing.length > 0 && (
              <ul className="space-y-1">
                {existing.map((s) => (
                  <li key={s.id}>
                    <Link to={wlFieldSheetPath(s.id)} className="text-sm font-semibold text-fi-amber underline">
                      {s.unitNumber || '—'}: the sheet is open
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {list.length === 0 ? (
              <PtRefusal message="This job has no unit yet. Ask the office." />
            ) : !canWrite ? (
              <PtRefusal message="Your role cannot open a day sheet." />
            ) : (
              <>
                <WlFormError message={error} />
                {list.length > 1 && (
                  <WlField label="Unit">
                    <select className={WL_INPUT} aria-label="Sheet unit" value={unitId} onChange={(e) => { setUnitId(e.target.value); setTemplateId(''); }}>
                      <option value="">Choose a unit</option>
                      {list.filter((u) => !openFor.has(u.unitId)).map((u) => (
                        <option key={u.unitId} value={u.unitId}>
                          {u.unitNumber || '—'}
                        </option>
                      ))}
                    </select>
                  </WlField>
                )}
                <WlField label="Copy crew from" hint="The unit's crews; change it on the sheet after">
                  <select className={WL_INPUT} aria-label="Copy crew from" value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={!chosen}>
                    <option value="">{unit.isLoading ? 'Loading…' : crews.length ? 'Do not copy; start with just me' : 'Start with just me'}</option>
                    {crews.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} · {c.members.length} {c.members.length === 1 ? 'hand' : 'hands'}
                      </option>
                    ))}
                  </select>
                </WlField>
                {!templateId && (
                  <WlField label="I am on this sheet as">
                    <select className={WL_INPUT} aria-label="My position" value={position} onChange={(e) => setPosition(e.target.value)}>
                      {POSITIONS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </WlField>
                )}
                <button
                  type="button"
                  className={`${PT_FIELD_PRIMARY} w-full`}
                  disabled={save.isPending || !chosen || openFor.has(chosen)}
                  onClick={() => {
                    setError(null);
                    if (!id || !workDate || !chosen) return;
                    const body = templateId
                      ? { unit_id: chosen, crew_template_id: templateId }
                      : { unit_id: chosen, crew: user?.id ? [{ user_id: user.id, position }] : [] };
                    save.mutate(
                      { jobId: id, workDate, body },
                      {
                        onSuccess: (res) => {
                          toast.success('Day sheet opened');
                          if (res.data?.id) navigate(wlFieldSheetPath(res.data.id), { replace: true });
                        },
                        onError: (err) => setError(wlJobRefusal(err, 'The day sheet could not be opened.')),
                      },
                    );
                  }}
                >
                  {save.isPending ? 'Opening…' : 'Open the sheet'}
                </button>
              </>
            )}
          </PtFieldCard>
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}

// ─── The sheet ───────────────────────────────────────────────────────────────

function FieldRecords({ sheet, canRecord }: { sheet: WlSheet; canRecord: boolean }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const draft = sheet.status === 'draft' && !sheet.locked;
  const tz = sheet.timeZone ?? null;
  const recordDrill = useRecordWlFieldDrill();
  const remove = useRemoveWlFieldRecord();
  const [drilling, setDrilling] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const rows = useMemo(
    () => [
      ...sheetTests(sheet).map((t) => ({ kind: 'test' as const, id: t.id, line: wlTestLine(t, tz), review: t.reviewStatus, by: t.recordedBy })),
      ...sheetDrills(sheet).map((d) => ({ kind: 'drill' as const, id: d.id, line: wlDrillLine(d, tz), review: d.reviewStatus, by: d.recordedBy })),
    ],
    [sheet, tz],
  );
  const crewIds = Array.from(new Set(sheet.crew.map((c) => c.userId).filter(Boolean)));
  const ref = sheet.jobNumber ? `${sheet.jobNumber} · ${wlDay(sheet.workDate)}` : undefined;

  return (
    <section className="space-y-3" data-testid="wl-field-records" data-can-record={canRecord ? 'true' : 'false'}>
      <h2 className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Pressure tests and drills</h2>
      {canRecord && (
        <div className="grid gap-2 sm:grid-cols-2">
          <button type="button" className={PT_FIELD_PRIMARY} onClick={() => navigate(`/field/wl/sheets/${sheet.id}/pressure-tests/new`)} data-testid="wl-field-record-test">
            Record pressure test
          </button>
          <button type="button" className={PT_FIELD_SECONDARY} onClick={() => setDrilling(true)} data-testid="wl-field-record-drill">
            Record drill
          </button>
        </div>
      )}
      <WlFormError message={rowError} />
      {rows.length === 0 ? (
        <p className="text-sm text-fieldiq-muted">No pressure test or drill is on this sheet. A rig-up test is recorded here before the first run.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={`${r.kind}-${r.id}`} data-testid={`wl-field-record-${r.kind}`}>
              <PtFieldCard className="flex items-start justify-between gap-2 px-4 py-3">
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-extrabold text-white">{r.kind === 'test' ? 'Pressure test' : 'Drill'}</p>
                  <p className="text-xs text-fieldiq-text">{r.line}</p>
                  <PtFieldBadge skin={{ ...pcReviewSkin(r.review), rail: '' }} />
                </div>
                {draft && r.review === 'pending' && !!userId && r.by === userId && (
                  <button
                    type="button"
                    className={WL_SECONDARY}
                    disabled={remove.isPending}
                    aria-label={`Remove this ${r.kind === 'test' ? 'pressure test' : 'drill'}`}
                    onClick={() => {
                      setRowError(null);
                      remove.mutate(
                        { sheetId: sheet.id, kind: r.kind, recordId: r.id },
                        {
                          onSuccess: () => toast.success(r.kind === 'test' ? 'Pressure test removed' : 'Drill removed'),
                          onError: (err) => setRowError(wlJobRefusal(err, 'The record could not be removed.')),
                        },
                      );
                    }}
                  >
                    Remove
                  </button>
                )}
              </PtFieldCard>
            </li>
          ))}
        </ul>
      )}
      {canRecord && (
        <RecordDrillDialog
          open={drilling}
          onClose={() => setDrilling(false)}
          target={{
            title: 'Record a crew drill on this sheet',
            defaultAt: wlDefaultInstant(sheet.startAt, sheet.endAt),
            defaultCrew: crewIds,
            defaultRef: ref,
            send: async (body) => {
              await recordDrill.mutateAsync({ sheetId: sheet.id, body });
            },
            refusal: wlJobRefusal,
            successMessage: 'Drill recorded. It is pending until the office approves the sheet.',
            note: 'The drill is pending until the office approves this sheet.',
          }}
        />
      )}
    </section>
  );
}

export function WlFieldSheetScreen() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const query = useWlFieldSheet(id);
  const sheet = query.data?.data ?? null;
  const catalog = useWlMeta().data?.data ?? null;
  const canRecord = !!sheet && canWriteWlSheets(user?.role) && wlCanRecordOnSheet(sheet, user?.id ?? null);
  // The pickers: a field hand may be refused the tenant lists; the editor falls back to the sheet's own rows.
  const users = useWlUsers(canRecord);
  const equipment = useWlEquipmentOptions(canRecord);
  const save = useSaveWlFieldSheet();
  const submit = useSubmitWlFieldSheet();
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<WlWarningEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const refused = refusedOpen(query.error);
  const draft = !!sheet && sheet.status === 'draft' && !sheet.locked;

  return (
    <PtFieldPage title={sheet ? `Day sheet · ${wlDay(sheet.workDate)}` : 'Day sheet'} backTo="/field/wl" testId="wl-field-sheet">
      {refused ? (
        <PtRefusal message={refused} testId="wl-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={query} subject="this day sheet">
          {!sheet ? (
            <PtRefusal message="That day sheet no longer exists. It may have been deleted." />
          ) : (
            <div className="space-y-4" data-editable={draft && canRecord ? 'true' : 'false'}>
              <div className="flex flex-wrap items-center gap-2">
                <PtFieldBadge skin={wlSheetStatusSkin(sheet.status, sheet.statusLabel)} />
                <span className="text-sm text-fieldiq-text">
                  {sheet.jobNumber || '—'} · {sheet.unitNumber || '—'} · {sheet.wellName || '—'}
                </span>
              </div>
              {sheet.status === 'draft' && sheet.returnedReason && (
                <p role="status" data-testid="wl-field-returned" className="rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
                  <span className="font-bold">Returned by the office: </span>
                  {sheet.returnedReason}
                </p>
              )}
              {draft && !canRecord && (
                <p className="text-sm text-fieldiq-muted" data-testid="wl-field-view-only">
                  The supervisor or the operator on this sheet records runs, tests and drills. You can view the sheet here.
                </p>
              )}
              <SheetWarnings sheet={sheet} extra={warnings} />
              <SheetSummary sheet={sheet} />
              {draft && canRecord ? (
                <>
                  <SheetEditor
                    key={`${sheet.id}-${query.dataUpdatedAt}`}
                    sheet={sheet}
                    catalog={catalog}
                    users={users.data ?? []}
                    equipment={(equipment.data ?? []).map((e) => ({ id: e.id, name: e.unitNumber ? `${e.name} · ${e.unitNumber}` : e.name }))}
                    rentIns={[]}
                    canEditCrew
                    pending={save.isPending}
                    error={error}
                    onSubmit={(body) => {
                      setError(null);
                      save.mutate(
                        { jobId: sheet.jobId, workDate: sheet.workDate, body: { ...body, unit_id: sheet.unitId } },
                        {
                          onSuccess: (res) => {
                            toast.success('Day sheet saved');
                            setWarnings(res.warnings);
                          },
                          onError: (err) => setError(wlJobRefusal(err, 'The day sheet could not be saved.')),
                        },
                      );
                    }}
                  />
                </>
              ) : (
                <SheetReadout sheet={sheet} />
              )}
              <SheetRuns sheet={sheet} runPath={fieldRunPath(sheet.id)} newRunPath={`/field/wl/sheets/${sheet.id}/runs/new`} canAdd={draft && canRecord} />
              <FieldRecords sheet={sheet} canRecord={draft && canRecord} />
              {draft && canRecord && (
                <button type="button" className={`${PT_FIELD_SECONDARY} w-full`} onClick={() => { setSubmitError(null); setSubmitting(true); }} data-testid="wl-field-submit">
                  Submit to the office
                </button>
              )}
            </div>
          )}
        </PtFieldQueryState>
      )}
      {sheet && (
        <WlDialog
          open={submitting}
          title="Submit day sheet"
          onClose={() => setSubmitting(false)}
          footer={
            <>
              <button type="button" className={PT_FIELD_SECONDARY} onClick={() => setSubmitting(false)} disabled={submit.isPending}>
                Not now
              </button>
              <button
                type="button"
                className={PT_FIELD_PRIMARY}
                disabled={submit.isPending}
                onClick={() =>
                  submit.mutate(sheet.id, {
                    onSuccess: () => {
                      toast.success('Day sheet submitted');
                      setSubmitting(false);
                    },
                    onError: (err) => setSubmitError(wlJobRefusal(err, 'The sheet could not be submitted.')),
                  })
                }
              >
                {submit.isPending ? 'Submitting…' : 'Submit'}
              </button>
            </>
          }
        >
          <WlFormError message={submitError} />
          <p className="text-sm text-fieldiq-text">Submits the saved sheet to the office. Save any changes first. A sheet needs crew to be submitted.</p>
        </WlDialog>
      )}
    </PtFieldPage>
  );
}

// ─── Runs ────────────────────────────────────────────────────────────────────

function useFieldRunOptions(enabled: boolean, keep: { lineId?: string | null; lineNumber?: string | null; toolStringId?: string | null } | null) {
  const lines = useWlLines();
  const strings = useWlToolStrings();
  const l = enabled ? (lines.data?.data ?? []).map((x) => ({ id: x.id, name: x.lineNumber })) : [];
  const t = enabled ? (strings.data?.data ?? []).map((x) => ({ id: x.id, name: x.name })) : [];
  if (keep?.lineId && !l.some((x) => x.id === keep.lineId)) l.push({ id: keep.lineId, name: keep.lineNumber || '—' });
  if (keep?.toolStringId && !t.some((x) => x.id === keep.toolStringId)) t.push({ id: keep.toolStringId, name: 'Tool string on the run' });
  return { lines: l, toolStrings: t };
}

const fieldMeta = (m: unknown): WlMeta | null => (m as WlMeta) ?? null;

export function WlFieldNewRunScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useWlFieldSheet(id);
  const sheet = query.data?.data ?? null;
  const catalog = useWlMeta().data?.data ?? null;
  const canRecord = !!sheet && sheet.status === 'draft' && !sheet.locked && wlCanRecordOnSheet(sheet, user?.id ?? null);
  const opts = useFieldRunOptions(canRecord, null);
  const add = useAddWlFieldRun();
  const [error, setError] = useState<string | null>(null);
  const [rigUp, setRigUp] = useState<string | null>(null);
  const back = id ? wlFieldSheetPath(id) : '/field/wl';

  return (
    <PtFieldPage title="Add a run" backTo={back} testId="wl-field-new-run">
      <PtFieldQueryState query={query} subject="this day sheet">
        {!sheet ? (
          <PtRefusal message="That day sheet no longer exists. It may have been deleted." />
        ) : !canRecord ? (
          <PtRefusal
            testId="wl-field-run-refused"
            message={sheet.status === 'draft' && !sheet.locked ? 'Only the supervisor or the operator on this sheet records runs.' : 'This sheet is no longer a draft.'}
          />
        ) : (
          <div className="space-y-4">
          {rigUp && (
            <div role="alert" data-testid="wl-rig-up-refused" className="space-y-3 rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text">
              <p className="font-semibold">{rigUp}</p>
              <button type="button" className={`${PT_FIELD_PRIMARY} w-full`} onClick={() => navigate(`/field/wl/sheets/${sheet.id}/pressure-tests/new`)}>
                Record the rig-up test
              </button>
            </div>
          )}
          <RunForm
            run={null}
            catalog={catalog}
            meta={fieldMeta(query.data?.meta)}
            lines={opts.lines}
            toolStrings={opts.toolStrings}
            pending={add.isPending}
            error={error}
            submitLabel="Add run"
            onCancel={() => navigate(back)}
            onSubmit={(body) => {
              setError(null);
              setRigUp(null);
              add.mutate(
                { sheetId: sheet.id, body },
                {
                  onSuccess: (res) => {
                    if (res.warnings.length) toast.warning(`Run saved with ${res.warnings.length} warning${res.warnings.length === 1 ? '' : 's'}`);
                    else toast.success('Run added');
                    navigate(res.data?.id ? fieldRunPath(sheet.id)(res.data.id) : back, { replace: true, state: { warnings: res.warnings } });
                  },
                  onError: (err) => {
                    const r = wlRigUpRefusal(err);
                    setRigUp(r);
                    setError(r ? null : wlJobRefusal(err, 'The run could not be added.'));
                  },
                },
              );
            }}
          />
          </div>
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}

export function WlFieldRunScreen() {
  const { id, runId } = useParams<{ id: string; runId: string }>();
  const { user } = useAuth();
  const query = useWlFieldSheet(id);
  const sheet = query.data?.data ?? null;
  const run = sheet?.runs.find((r) => r.id === runId) ?? null;
  const catalog = useWlMeta().data?.data ?? null;
  const canRecord = !!sheet && sheet.status === 'draft' && !sheet.locked && wlCanRecordOnSheet(sheet, user?.id ?? null);
  const [editing, setEditing] = useState(false);
  const opts = useFieldRunOptions(canRecord && editing, run);
  const update = useUpdateWlFieldRun();
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const [warnings, setWarnings] = useState<WlWarningEntry[]>(
    ((location.state as { warnings?: WlWarningEntry[] } | null)?.warnings ?? []).filter((w) => w.code !== 'misfire_count_mismatch'),
  );
  const back = id ? wlFieldSheetPath(id) : '/field/wl';

  return (
    <PtFieldPage title={run ? `Run ${run.runNumber} · ${run.runKindLabel ?? run.runKind}` : 'Run'} backTo={back} testId="wl-field-run">
      <PtFieldQueryState query={query} subject="this run">
        {!run ? (
          <PtRefusal message="That run no longer exists. It may have been deleted." />
        ) : editing && canRecord ? (
          <RunForm
            key={run.updatedAt ?? run.id}
            run={run}
            catalog={catalog}
            meta={fieldMeta(query.data?.meta)}
            lines={opts.lines}
            toolStrings={opts.toolStrings}
            pending={update.isPending}
            error={error}
            submitLabel="Save run"
            onCancel={() => setEditing(false)}
            onSubmit={(body) => {
              setError(null);
              update.mutate(
                { id: run.id, body },
                {
                  onSuccess: (res) => {
                    toast.success('Run saved');
                    setWarnings(res.warnings.filter((w) => w.code !== 'misfire_count_mismatch'));
                    setEditing(false);
                  },
                  onError: (err) => setError(wlRunRefusal(err, 'The run could not be saved.')),
                },
              );
            }}
          />
        ) : (
          <div className="space-y-4">
            <RunWarnings warnings={warnings} testId="wl-run-created-warnings" />
            <RunReadout run={run} />
            {canRecord && (
              <button type="button" className={`${PT_FIELD_PRIMARY} w-full`} onClick={() => { setError(null); setEditing(true); }} data-testid="wl-field-edit-run">
                Edit run
              </button>
            )}
          </div>
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}

// ─── Record a pressure test ──────────────────────────────────────────────────

export function WlFieldTestNewScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useWlFieldSheet(id);
  const record = useRecordWlFieldTest();
  const sheet = query.data?.data ?? null;

  if (sheet && id && !(sheet.status === 'draft' && !sheet.locked && wlCanRecordOnSheet(sheet, user?.id ?? null))) {
    return (
      <PtFieldPage title="Record pressure test" backTo={wlFieldSheetPath(id)} testId="wl-field-test-new">
        <PtRefusal
          testId="wl-field-test-new-refused"
          message={
            sheet.status === 'draft' && !sheet.locked
              ? 'Only the supervisor or the operator on this sheet can record a pressure test. The rest of the crew can view them on the sheet.'
              : 'This sheet is no longer a draft, so a pressure test cannot be recorded on it.'
          }
        />
      </PtFieldPage>
    );
  }
  if (!sheet || !id) {
    return (
      <PtFieldPage title="Record pressure test" backTo={id ? wlFieldSheetPath(id) : '/field/wl'} testId="wl-field-test-new">
        <PtFieldQueryState query={query} subject="this day sheet">
          <PtRefusal message="That day sheet no longer exists. It may have been deleted." />
        </PtFieldQueryState>
      </PtFieldPage>
    );
  }
  return (
    <PressureControlTestNewScreen
      key={sheet.id}
      target={{
        eyebrow: `Day sheet · ${wlDay(sheet.workDate)}`,
        title: 'Record a pressure test',
        defaultAt: wlDefaultInstant(sheet.startAt, sheet.endAt),
        defaultRef: sheet.jobNumber ? `${sheet.jobNumber} · ${wlDay(sheet.workDate)}` : undefined,
        send: async (body) => {
          const out = await record.mutateAsync({ sheetId: sheet.id, body });
          return { warnings: out.warnings };
        },
        refusal: wlJobRefusal,
        successMessage: 'Pressure test recorded. It is pending until the office approves the sheet.',
        done: () => navigate(wlFieldSheetPath(sheet.id), { replace: true }),
      }}
    />
  );
}
